import React, { useState } from "react";
import axios from "axios";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProjectForm = () => {
  const { t } = useLanguage();
  const [form, setForm] = useState({
    name: "",
    email: "",
    project_type: "",
    budget_range: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const onChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/projects`, form);
      setSuccess(true);
      toast.success(t.form.success);
      setForm({
        name: "",
        email: "",
        project_type: "",
        budget_range: "",
        message: "",
      });
    } catch (err) {
      console.error(err);
      toast.error(t.form.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="project-form"
      className="py-24 lg:py-32 bg-[#DDE5DD]"
      data-testid="project-form-section"
    >
      <div className="max-w-4xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#5C6B5D]">
            {t.form.label}
          </span>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05] text-[#202C21] font-semibold">
            {t.form.title}
          </h2>
          <p className="mt-4 text-base text-[#5C6B5D] leading-relaxed">
            {t.form.subtitle}
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          className="mt-12 bg-white rounded-[28px] p-8 lg:p-12 border border-[rgba(117,148,122,0.2)] shadow-[0_12px_48px_rgba(32,44,33,0.06)]"
          data-testid="project-form"
        >
          {success ? (
            <div
              className="flex flex-col items-center justify-center text-center py-10"
              data-testid="project-form-success"
            >
              <div className="w-14 h-14 rounded-full bg-[#E6EDE7] flex items-center justify-center">
                <CheckCircle2
                  className="w-7 h-7 text-[#5F7C63]"
                  strokeWidth={1.75}
                />
              </div>
              <h3 className="mt-5 font-heading text-2xl font-semibold text-[#202C21]">
                {t.form.success}
              </h3>
              <button
                type="button"
                onClick={() => setSuccess(false)}
                className="mt-6 text-sm text-[#5F7C63] hover:text-[#202C21] underline-offset-4 hover:underline"
                data-testid="project-form-reset"
              >
                {t.form.submit} →
              </button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-5">
              <div className="flex flex-col gap-2">
                <label className="text-xs uppercase tracking-[0.18em] font-bold text-[#5C6B5D]">
                  {t.form.name}
                </label>
                <input
                  required
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  className="bg-white border border-[rgba(117,148,122,0.25)] rounded-xl px-4 py-3 text-base focus:ring-2 focus:ring-[#75947A] focus:border-transparent focus:outline-none transition"
                  data-testid="form-name"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs uppercase tracking-[0.18em] font-bold text-[#5C6B5D]">
                  {t.form.email}
                </label>
                <input
                  required
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={onChange}
                  className="bg-white border border-[rgba(117,148,122,0.25)] rounded-xl px-4 py-3 text-base focus:ring-2 focus:ring-[#75947A] focus:border-transparent focus:outline-none transition"
                  data-testid="form-email"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs uppercase tracking-[0.18em] font-bold text-[#5C6B5D]">
                  {t.form.project_type}
                </label>
                <input
                  required
                  name="project_type"
                  value={form.project_type}
                  onChange={onChange}
                  placeholder={t.form.project_type_placeholder}
                  className="bg-white border border-[rgba(117,148,122,0.25)] rounded-xl px-4 py-3 text-base focus:ring-2 focus:ring-[#75947A] focus:border-transparent focus:outline-none transition placeholder:text-[#8A9A8B]"
                  data-testid="form-project-type"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="text-xs uppercase tracking-[0.18em] font-bold text-[#5C6B5D]">
                  {t.form.budget}
                </label>
                <select
                  required
                  name="budget_range"
                  value={form.budget_range}
                  onChange={onChange}
                  className="bg-white border border-[rgba(117,148,122,0.25)] rounded-xl px-4 py-3 text-base focus:ring-2 focus:ring-[#75947A] focus:border-transparent focus:outline-none transition text-[#202C21]"
                  data-testid="form-budget"
                >
                  <option value="" disabled>
                    {t.form.budget_placeholder}
                  </option>
                  {t.form.budget_options.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-2 md:col-span-2">
                <label className="text-xs uppercase tracking-[0.18em] font-bold text-[#5C6B5D]">
                  {t.form.message}
                </label>
                <textarea
                  required
                  name="message"
                  rows={5}
                  value={form.message}
                  onChange={onChange}
                  placeholder={t.form.message_placeholder}
                  className="bg-white border border-[rgba(117,148,122,0.25)] rounded-xl px-4 py-3 text-base focus:ring-2 focus:ring-[#75947A] focus:border-transparent focus:outline-none transition resize-y placeholder:text-[#8A9A8B]"
                  data-testid="form-message"
                />
              </div>
              <div className="md:col-span-2 flex items-center justify-end mt-2">
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center gap-2 bg-[#75947A] hover:bg-[#5F7C63] disabled:opacity-70 text-white rounded-full px-7 py-3.5 text-sm font-medium transition-colors"
                  data-testid="form-submit"
                >
                  {submitting ? t.form.sending : t.form.submit}
                  <ArrowRight className="w-4 h-4" strokeWidth={2} />
                </button>
              </div>
            </div>
          )}
        </form>
      </div>
    </section>
  );
};

export default ProjectForm;
